import { useMemo, useState } from "react";
import { CalendarIcon, ChevronDownIcon, InfoIcon } from "lucide-react";
import { GlobalSearchBar } from "../components/GlobalSearchBar";
import { EmployeeRosterTable } from "../components/payroll/EmployeeRosterTable";
import { ExportModal } from "../components/payroll/ExportModal";
import { PayPeriodStatusBar } from "../components/payroll/PayPeriodStatusBar";
import { SubmitForApprovalModal } from "../components/payroll/SubmitForApprovalModal";
import { UnlockPeriodModal } from "../components/payroll/UnlockPeriodModal";
import type { PayrollDashboardPageProps } from "../types";

export function PayrollDashboardPage({
  periods,
  selectedPeriodId,
  employees,
  providerTemplateRefs,
  isBusy = false,
  routeLoading = false,
  routeError = false,
  onSelectPeriod,
  onOpenEmployee,
  onSubmitForApproval,
  onUnlockPeriod,
  onExportPeriod,
  onAddEmployee,
  onImportCsv,
  onAddPosition,
  onAddStipend,
  onSuspendEmployee,
}: PayrollDashboardPageProps) {
  const [isPeriodMenuOpen, setIsPeriodMenuOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const currentPeriod =
    periods.find((period) => period.id === selectedPeriodId) ?? periods[0];

  const filteredEmployees = useMemo(() => {
    if (!searchQuery) {
      return employees;
    }

    const query = searchQuery.toLowerCase();
    const compAboveMatch = query.match(
      /(?:annual\s+)?comp(?:ensation)?\s+above\s+\$?([0-9,]+)/i,
    );
    if (compAboveMatch) {
      const threshold = parseInt(compAboveMatch[1].replace(/,/g, ""), 10);
      return employees.filter((employee) => employee.annualCompTotal > threshold);
    }

    const compBelowMatch = query.match(
      /(?:annual\s+)?comp(?:ensation)?\s+below\s+\$?([0-9,]+)/i,
    );
    if (compBelowMatch) {
      const threshold = parseInt(compBelowMatch[1].replace(/,/g, ""), 10);
      return employees.filter((employee) => employee.annualCompTotal < threshold);
    }

    if (query.includes("status:")) {
      const statusSearch = query.split("status:")[1]?.trim() ?? "";
      return employees.filter((employee) =>
        employee.status.toLowerCase().includes(statusSearch),
      );
    }

    return employees.filter((employee) => {
      const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
      return (
        fullName.includes(query) ||
        employee.employeeId.toLowerCase().includes(query) ||
        employee.status.toLowerCase().includes(query) ||
        employee.track.toLowerCase().includes(query)
      );
    });
  }, [employees, searchQuery]);

  const handleActionClick = (action: string) => {
    if (action === "export") {
      setIsExportModalOpen(true);
    } else if (action === "unlock") {
      setIsUnlockModalOpen(true);
    } else if (action === "submit") {
      setIsSubmitModalOpen(true);
    }
  };

  if (!currentPeriod) {
    return null;
  }

  return (
    <div
      data-testid="payroll-dashboard-page-root"
      data-route-ready={!routeLoading && !routeError ? "true" : "false"}
      data-route-loading={routeLoading ? "true" : "false"}
      data-route-error={routeError ? "true" : "false"}
      className="min-h-full bg-background-light p-6 font-body"
    >
      <div className="mx-auto max-w-[1440px]">
        <div className="mb-8 flex min-w-0 items-center justify-between gap-4">
          <h1 className="min-w-0 font-heading text-2xl font-semibold text-iron-grey">
            Payroll Prep
          </h1>

          <div className="relative shrink-0">
            <button
              onClick={() => setIsPeriodMenuOpen((current) => !current)}
              disabled={isBusy}
              data-testid="payroll-period-menu-trigger"
              className="flex min-w-0 items-center gap-2 rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CalendarIcon className="h-4 w-4 text-text-secondary" />
              {currentPeriod.label}
              <ChevronDownIcon className="ml-1 h-4 w-4 text-text-secondary" />
            </button>

            {isPeriodMenuOpen ? (
              <div className="absolute right-0 top-full z-50 mt-1 w-64 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg animate-in fade-in zoom-in-95 duration-100">
                <div className="border-b border-border px-3 py-2">
                  <span className="font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Academic Year 2026-2027
                  </span>
                </div>
                {periods.map((period) => (
                  <button
                    key={period.id}
                    onClick={() => {
                      setIsPeriodMenuOpen(false);
                      void onSelectPeriod(period.id);
                    }}
                    data-testid={`payroll-period-option-${period.id}`}
                    className={`flex w-full items-center justify-between px-4 py-2.5 text-left font-body text-sm transition-colors hover:bg-muted ${
                      selectedPeriodId === period.id
                        ? "bg-primary/5 font-semibold text-primary"
                        : "text-iron-grey"
                    }`}
                  >
                    {period.label}
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                        period.status === "In Progress"
                          ? "bg-info-background text-primary"
                          : period.status === "Exported"
                            ? "bg-muted text-text-secondary"
                            : "bg-muted text-iron-grey"
                      }`}
                    >
                      {period.status}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <GlobalSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search employees, positions, or type 'Annual comp above $90,000'..."
        />

        {currentPeriod.hasHoliday ? (
          <div className="mb-6 flex items-start gap-3 rounded-r-[8px] border-l-4 border-coral bg-warning-background/50 p-4">
            <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-coral" />
            <div>
              <h3 className="font-body text-sm font-semibold text-iron-grey">
                Jewish Holiday in this Period: {currentPeriod.holidayName} (
                {currentPeriod.holidayDates})
              </h3>
              <p className="mt-1 font-body text-sm text-text-secondary">
                {currentPeriod.holidayRule}
              </p>
            </div>
          </div>
        ) : null}

        <PayPeriodStatusBar
          period={currentPeriod}
          onActionClick={handleActionClick}
        />

        <EmployeeRosterTable
          employees={filteredEmployees}
          onOpenEmployee={onOpenEmployee}
          onAddEmployee={onAddEmployee}
          onImportCsv={onImportCsv}
          onAddPosition={onAddPosition}
          onAddStipend={onAddStipend}
          onSuspendEmployee={onSuspendEmployee}
        />

        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          period={currentPeriod}
          providerTemplateRefs={providerTemplateRefs}
          incompleteCount={Math.max(
            currentPeriod.totalEmployees - currentPeriod.employeesComplete,
            0,
          )}
          onConfirm={onExportPeriod}
        />

        <UnlockPeriodModal
          isOpen={isUnlockModalOpen}
          onClose={() => setIsUnlockModalOpen(false)}
          period={currentPeriod}
          onConfirm={(reason) => {
            void onUnlockPeriod?.(reason);
          }}
        />

        <SubmitForApprovalModal
          isOpen={isSubmitModalOpen}
          onClose={() => setIsSubmitModalOpen(false)}
          period={currentPeriod}
          totalPayroll={currentPeriod.totalGross}
          onConfirm={(notes) => {
            void onSubmitForApproval?.(notes);
          }}
        />
      </div>
    </div>
  );
}
